import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  useTheme,
  useMediaQuery,
  Button,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';

import {
  Group as GroupIcon,
  Chat as ChatIcon,
  LocationOn as LocationOnIcon,
  Interests as InterestsIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Wc as WcIcon,
  Public as PublicIcon,
  ExitToApp as LeaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { apiServices } from '../../../../services/apiServices';
import GroupChat from '../GroupChat';

const GroupCategories = ({ selectedMenu, selectedGroup, onGroupSelect, onBackToCategories }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useSelector((state) => state.auth);

  // State management
  const [groups, setGroups] = useState([]);
  const [userMemberships, setUserMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joiningGroup, setJoiningGroup] = useState(null);
  const [leavingGroup, setLeavingGroup] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Icon mapping for groups
  const iconMap = {
    'location': <LocationOnIcon sx={{ fontSize: 40, color: '#E4E1D2' }} />,
    'interest': <InterestsIcon sx={{ fontSize: 40, color: '#f44336' }} />,
    'profession': <WorkIcon sx={{ fontSize: 40, color: '#ff9800' }} />,
    'business': <BusinessIcon sx={{ fontSize: 40, color: '#4caf50' }} />,
    'age': <PersonIcon sx={{ fontSize: 40, color: '#000000' }} />,
    'gender': <WcIcon sx={{ fontSize: 40, color: '#03a9f4' }} />,
    'culture': <PublicIcon sx={{ fontSize: 40, color: '#ffeb3b' }} />
  };

  // Fetch groups and user memberships on component mount
  useEffect(() => {
    fetchGroupsAndMemberships();
  }, []);

  const fetchGroupsAndMemberships = async () => {
    setLoading(true);
    try {
      // Fetch all groups
      const groupsResponse = await apiServices.getAPI('groups');
      if (groupsResponse.data?.success) {
        // Sort groups by group_id
        const sortedGroups = groupsResponse.data.groups.sort((a, b) => a.group_id - b.group_id);
        setGroups(sortedGroups);
      } else {
        console.error('Groups API response:', groupsResponse.data);
        setError('Invalid response from groups API');
      }

      // Fetch user's group memberships
      if (user?.peacekeeper_id) {
        const membershipsResponse = await apiServices.getAPI(`users/${user.peacekeeper_id}/groups`);
        if (membershipsResponse.data?.success) {
          setUserMemberships(membershipsResponse.data.memberships);
        } else {
          console.error('Memberships API response:', membershipsResponse.data);
        }
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      if (error.response?.status === 404) {
        setError('Groups API not found. Please check if the server is running.');
      } else if (error.response?.status === 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('Failed to load groups. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleJoinGroup = async (group) => {
    if (!user?.peacekeeper_id) {
      setError('User information not available');
      return;
    }

    setJoiningGroup(group.group_id);
    try {
      const response = await apiServices.postAPI(`groups/${group.group_id}/join`, {
        peacekeeperId: user.peacekeeper_id
      });

      if (response.data?.success) {
        setSuccessMessage(`Successfully joined ${group.name}`);
        // Refresh groups and memberships
        await fetchGroupsAndMemberships();
      } else {
        setError(response.data?.error || 'Failed to join group');
      }
    } catch (error) {
      console.error('Error joining group:', error);
      setError('Failed to join group. Please try again.');
    } finally {
      setJoiningGroup(null);
    }
  };

  const handleLeaveGroup = async (group) => {
    if (!user?.peacekeeper_id) {
      setError('User information not available');
      return;
    }

    setLeavingGroup(group.group_id);
    try {
      const response = await apiServices.postAPI(`groups/${group.group_id}/leave`, {
        peacekeeperId: user.peacekeeper_id
      });

      if (response.data?.success) {
        setSuccessMessage(`Successfully left ${group.name}`);
        // Refresh groups and memberships
        await fetchGroupsAndMemberships();
      } else {
        setError(response.data?.error || 'Failed to leave group');
      }
    } catch (error) {
      console.error('Error leaving group:', error);
      setError('Failed to leave group. Please try again.');
    } finally {
      setLeavingGroup(null);
    }
  };

  const handleGroupClick = (group) => {
    // Check if user is a member of this group
    const isMember = userMemberships.some(membership => membership.group_id === group.group_id);
    if (isMember) {
      onGroupSelect(group);
    } else {
      setError('You must join this group to access the chat');
    }
  };

  const isUserMember = (group) => {
    return userMemberships.some(membership => membership.group_id === group.group_id);
  };

  // If a group is selected, show the GroupChat component
  if (selectedGroup) {
    console.log('selectedGroup', selectedGroup);
    return (
      <Box sx={{ flex: 1 }} className='chat-Main-Component'>
        <GroupChat
          groupId={selectedGroup.group_id}
          groupName={selectedGroup.name}
          groupKey={selectedGroup.id}
        />
      </Box>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show group categories
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }} className='main-grchat-component'>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography className='green-title cat-title' variant="h5">
            7 Club Categories
          </Typography>

        </Box>

        <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Connect with peacekeepers based on your preferences and interests
          </Typography>
        </Box>
      </Box>

      {/* Group Categories Grid */}
      <Grid container spacing={2}>
        {groups.map((group) => {
          const isMember = isUserMember(group);
          const isLoading = joiningGroup === group.group_id || leavingGroup === group.group_id;

          return (
            <Grid size={{ xs: 6, md: 4, sm: 6 }} key={group.group_id} sx={{ width: '100%', padding: "10px 30px" }}>
              <Card
                sx={{
                  height: 280,
                  cursor: isMember ? 'pointer' : 'default',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: isLoading ? 0.7 : 1,

                  '&:hover': {
                    transform: isMember ? 'translateY(-4px)' : 'none',
                    boxShadow: isMember ? theme.shadows[8] : theme.shadows[1],
                    borderLeft: `4px solid ${group.color}`
                  }
                }}
                onClick={isMember ? () => handleGroupClick(group) : undefined}
              >
                <CardContent sx={{
                  p: 1,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  justifyContent: 'space-between'
                }}>
                  <Box>
                    <Box>
                      {iconMap[group.id] || <GroupIcon sx={{ fontSize: 40, color: group.color }} />}
                    </Box>

                    <Typography fontWeight="bold" gutterBottom sx={{ mb: 1 }}>
                      {group.name}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: '40px' }}>
                      {group.description}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                      <Chip
                        label={`${group.member_count} members`}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                      {/* <Chip
                        label={`${group.active_users} active`}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        sx={{ fontSize: '0.7rem' }}
                      /> */}
                    </Box>
                  </Box>

                  <Button
                    variant={isMember ? "outlined" : "contained"}
                    startIcon={isMember ? <ChatIcon /> : <GroupIcon />}
                    fullWidth
                    disabled={isLoading}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isMember) {
                        handleGroupClick(group);
                      } else {
                        handleJoinGroup(group);
                      }
                    }}
                    sx={{
                      borderRadius: 2,
                      bgcolor: isMember ? 'transparent' : group.color,
                      color: isMember ? group.color : 'white',
                      borderColor: group.color,
                      '&:hover': {
                        bgcolor: isMember ? group.color : group.color,
                        color: isMember ? 'white' : 'white',
                        opacity: 0.9
                      }
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : isMember ? (
                      'OPEN CHAT'
                    ) : (
                      'JOIN GROUP'
                    )}
                  </Button>

                  {isMember && (
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<LeaveIcon />}
                      disabled={isLoading}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLeaveGroup(group);
                      }}
                      sx={{
                        mt: 1,
                        color: 'error.main',
                        fontSize: '0.75rem',
                        '&:hover': {
                          bgcolor: 'error.light',
                          color: 'white'
                        }
                      }}
                    >
                      Leave Group
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GroupCategories;